import React from 'react';
import { cloneDeep } from 'lodash';
import { useParams } from 'react-router-dom';

import {
  postTeamStateAsync,
  teamDistributeTitleAsync
} from '../services/teams';
import {
  getStepTaskCompletedMap,
  executeControlStructureStepEventHandler
} from '../utils/game-engine/game';
import {
  TeamGiveItemAsync,
  TeamGiveTitleAsync,
  TeamRemoveItemAsync,
  TeamRemoveTitleAsync,
  GiveScore as TeamGiveScore,
  RemoveScore as TeamRemoveScore
} from '../utils/game-engine/team';
import {
  UpdateTeamStateAsync,
  UpdatePlayerStateAsync
} from '../utils/game-engine/base';
import {
  AddTask,
  TaskExistsAsync,
  GetTaskById as GetPlayerTaskById
} from '../utils/game-engine/assets';
import {
  OpenTask,
  GiveScore,
  ShowTaskAsync,
  IsStepExecuted,
  AddExecutedStepAsync,
  PlayerAlgorithmHandler
} from '../utils/game-engine/player';
import { useTimers } from './use-game-timer';
import { TeamContext } from '../contexts/team';
import { GameContext } from '../contexts/game';
import { apiFetcher } from '../utils/fetchers';
import { useGameToast } from './use-game-toast';
import { useBankAction } from './use-inventory-bank';
import {
  GetTaskContentById,
  isContainPremessageOrIntro
} from '../utils/game-document/assets/task-contents';
import { AreaEntity } from '../types/game-document/entities';
import { AddTitle } from '../utils/game-engine/assets/titles';
import { giveScore as teamGiveScore } from '../services/teams';
import { GameDocumentContext } from '../contexts/game-document';
import { TeamAlgorithmHandler } from '../utils/game-engine/team';
import { PlayerContext, PlayerContextState } from '../contexts/player';
import { AlgorithmControlStructure, AlgorithmStep } from '../types/algorithm';
import { BankActionResponse, BankItem } from '../types/responses/bank-response';
import { distributeTaskAsync, distributeTitleAsync } from '../services/players';
import { GetTaskById, GetTimerObjectById } from '../utils/game-document/assets';

export const useGameEvent = () => {
  const { gameCode } = useParams();

  const [player, setPlayer] = React.useContext(PlayerContext);
  const [team, setTeam] = React.useContext(TeamContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [game, , gameConnection] = React.useContext(GameContext);

  const teamCode = player.playerState?.teamCode;

  const { start, pause, stop } = useTimers();

  const {
    addItemToast,
    addScoreToast,
    addTitleToast,
    removeItemToast,
    removeScoreToast,
    removeTitleToast,
    showScoreNotification
  } = useGameToast();

  const { trigger } = useBankAction(gameCode ?? '');

  const inGameSetting = React.useMemo(
    () =>
      game.gameState?.runtime?.inGame ??
      gameDocument.gameDocument?.settings.inGame,
    [
      game.gameState?.runtime?.inGame,
      gameDocument.gameDocument?.settings.inGame
    ]
  );

  const getProximitySettings = React.useCallback(
    (taskId: string) => {
      const selectedTask = GetTaskById(gameDocument.gameDocument, taskId);
      let tasksRequireProximity = inGameSetting?.tasksRequireProximity;
      let enablePremessageOutsideRange =
        inGameSetting?.enablePremessageOutsideRange;
      let setTaskProximity = parseInt(inGameSetting?.setTaskProximity ?? '0');

      if (selectedTask && selectedTask.settingsOverride) {
        tasksRequireProximity = true;
        enablePremessageOutsideRange =
          selectedTask.settingsOverride.enablePremessageOutsideRange;
        setTaskProximity = selectedTask.settingsOverride.setTaskProximity ?? 0;
      }

      return {
        tasksRequireProximity,
        enablePremessageOutsideRange,
        setTaskProximity
      };
    },
    [
      inGameSetting?.tasksRequireProximity,
      inGameSetting?.enablePremessageOutsideRange,
      inGameSetting?.setTaskProximity
    ]
  );

  const openTaskContent = React.useCallback(
    async (
      id: string,
      isOnClick?: boolean,
      isAccesible: boolean = true,
      playerTemp?: PlayerContextState
    ) => {
      const proximitySettings = getProximitySettings(id);
      if (
        proximitySettings.tasksRequireProximity &&
        !isAccesible &&
        !proximitySettings.enablePremessageOutsideRange
      )
        return;

      const task = gameDocument.gameDocument?.assets?.tasks?.find(
        (x) => x.id === id
      );
      if (task) {
        if (task.taskContentId && task.taskContentId !== '') {
          const { premessage } = isContainPremessageOrIntro(
            gameDocument.gameDocument!,
            task.taskContentId,
            player?.playerState?.language?.name!
          );

          const handlePlayerUpdate = () => {
            setPlayer(() => {
              const response = OpenTask(playerTemp!, task.id);
              return UpdatePlayerStateAsync(playerTemp!, response);
            });
          };

          if (inGameSetting?.tasksRequireProximity) {
            if ((!isAccesible && premessage && isOnClick) || isAccesible) {
              handlePlayerUpdate();
              return;
            }
          } else {
            handlePlayerUpdate();
          }
        } else {
          const isTaskExist = await TaskExistsAsync(player, task.id);
          if (!isTaskExist) {
            setPlayer(() => {
              const response = AddTask(playerTemp!, task);
              return UpdatePlayerStateAsync(playerTemp!, response);
            });
          }

          // if task doesnt has task content, it will auto complete when open, then execute onComplete event
          await executeTaskEvent(
            task.id,
            ['onOpenTask', 'onCompleteTask', 'onCloseTask'],
            playerTemp
          );
        }
      }
    },
    [
      player,
      inGameSetting?.tasksRequireProximity,
      gameDocument.gameDocument?.assets?.tasks,
      setPlayer,
      getProximitySettings
    ]
  );

  const toastHandler = async (
    player: PlayerContextState,
    step: AlgorithmStep | AlgorithmControlStructure
  ) => {
    const { argumentAssetId = '', operation = '' } = step;
    switch (operation) {
      case 'openTask':
        await openTaskContent(argumentAssetId as string, true, true, player);
        break;
      case 'giveScore':
        addScoreToast(step?.argumentQuantity ?? 0, step?.identifier === 'team');
        break;
      case 'removeScore':
        removeScoreToast(
          step?.argumentQuantity ?? 0,
          step?.identifier === 'team'
        );
        break;
      case 'giveTitle':
        addTitleToast(
          step?.argumentAssetId as string,
          step?.identifier === 'team'
        );
        break;
      case 'removeTitle':
        removeTitleToast(
          step?.argumentAssetId as string,
          step?.identifier === 'team'
        );
        break;
      case 'giveItem':
        addItemToast(
          step.argumentAssetId as string,
          step.argumentQuantity ?? 0,
          step?.identifier === 'team'
        );
        break;
      case 'removeItem':
        removeItemToast(
          step?.argumentAssetId as string,
          step?.argumentQuantity ?? 0,
          step?.identifier === 'team'
        );
        break;
      case 'pauseTimer':
        pause(step?.argumentAssetId as string);
        break;
      case 'stopTimer':
        stop(step?.argumentAssetId as string);
        break;
      case 'startTimer':
      case 'resumeTimer':
        start(step?.argumentAssetId as string);
        break;
      default:
        break;
    }
  };

  const executeTimerEvent = async (
    timerId: string,
    type: 'onStartTimer' | 'onStopTimer' | 'onElapsedTimer'
  ) => {
    const timer = GetTimerObjectById(gameDocument.gameDocument!, timerId);

    if (timer && timer.events) {
      const eventSteps = timer.events[type]?.steps ?? [];
      const updatedPlayer = await executeEvent(eventSteps);
      setPlayer(updatedPlayer);
    }
  };

  const executeTaskEvent = async (
    taskId: string,
    events: ('onOpenTask' | 'onCompleteTask' | 'onCloseTask' | 'onProximity')[],
    playerProps?: typeof player
  ) => {
    const playerContext = playerProps !== undefined ? playerProps : player;

    if (
      game.gameState?.status === 'Finished' ||
      game.gameState?.status === 'PostGame'
    )
      return playerContext;

    const task = GetTaskById(gameDocument.gameDocument, taskId);

    if (task && task.events) {
      let eventSteps: (AlgorithmStep | AlgorithmControlStructure)[] = [];

      for (const type of events) {
        eventSteps = [...eventSteps, ...(task.events[type]?.steps ?? [])];
        if (type === 'onCompleteTask') {
          // remove giveScore(score) step if doesnt have task content
          if (!task.taskContentId)
            eventSteps = eventSteps.filter(
              (step) => step.operation !== 'giveScore(score)'
            );

          // remove giveScore(score) step if have task content but no form
          if (task.taskContentId) {
            const selectedTaskContent = GetTaskContentById(
              gameDocument.gameDocument,
              task.taskContentId
            );
            if (
              selectedTaskContent &&
              (!selectedTaskContent.forms ||
                (selectedTaskContent.forms &&
                  !selectedTaskContent.forms.length))
            ) {
              eventSteps = eventSteps.filter(
                (step) => step.operation !== 'giveScore(score)'
              );
            }
          }

          // add global onTaskComplete global setting in map location
          // will be executed after all onComplete from task event executed
          const location = playerContext.playerState!.location!;
          if (location !== undefined && location !== '') {
            const steps = getStepTaskCompletedMap(location, gameDocument);
            eventSteps.push(...steps);
          }
        }
      }

      const updatedPlayer = await executeEvent(eventSteps, playerContext);
      return updatedPlayer;
    }

    return playerContext;
  };

  const executeAreaEvent = async (
    area: AreaEntity,
    type: 'onEntry' | 'onExit'
  ) => {
    if (area.events) {
      const eventSteps = area.events[type]?.steps ?? [];
      const updatedPlayer = await executeEvent(eventSteps);
      setPlayer(updatedPlayer);
    }
  };

  const getMapOrZoneEventSteps = (
    location: string,
    type: 'onEnterMap' | 'onExitMap'
  ) => {
    let eventSteps: AlgorithmStep[] | AlgorithmControlStructure[] = [];

    if (location === 'world-map') {
      const worldMap = gameDocument?.gameDocument?.rules?.worldMap;
      if (worldMap) {
        eventSteps = worldMap.events[type]?.steps ?? [];
      }
    } else {
      const zone = gameDocument?.gameDocument?.assets?.zones?.find(
        (x) => x.id === location
      );
      if (zone && zone.events) {
        eventSteps = zone.events[type]?.steps ?? [];
      }
    }

    return eventSteps;
  };

  const executeMapOrZoneEvent = async (
    prevLocation: string | null | undefined,
    location: string
  ) => {
    let eventSteps: AlgorithmStep[] | AlgorithmControlStructure[] = [];
    let exit: AlgorithmStep[] | AlgorithmControlStructure[] = [];
    let enter: AlgorithmStep[] | AlgorithmControlStructure[] = [];

    if (
      prevLocation !== undefined &&
      prevLocation !== null &&
      prevLocation !== ''
    )
      exit = getMapOrZoneEventSteps(prevLocation, 'onExitMap');

    enter = getMapOrZoneEventSteps(location, 'onEnterMap');

    // whenever player move to new location, player will process these 2 event
    // exit the previous location, and go to the new location (or we can say current location)
    // add exit previous location steps event first and then add enter current location steps event
    // steps are collected and ready to be executed sequentially
    eventSteps = [...exit, ...enter];

    const updatedPlayer = await executeEvent(eventSteps);
    setPlayer(updatedPlayer);
  };

  const executeEvent = async (
    steps: AlgorithmStep[] | AlgorithmControlStructure[],
    playerProps?: typeof player
  ) => {
    let playerTemp = cloneDeep(
      playerProps !== undefined ? playerProps : player
    );

    for (const step of steps) {
      // if identifier is team, but player has no team, skip the step
      if (
        step.identifier === 'team' &&
        (teamCode === '' || teamCode === undefined)
      )
        continue;

      // if step already executed but step isnt onTaskCompleted action, skip the step
      if (
        IsStepExecuted(playerTemp, step.id) &&
        step.action !== 'WorldMap.onTaskCompleted'
      )
        continue;

      playerTemp = UpdatePlayerStateAsync(
        playerTemp,
        AddExecutedStepAsync(playerTemp, step?.id)
      );

      if ((step as AlgorithmControlStructure).condition) {
        // execute conditional step
        const controlStructureResponse =
          await executeControlStructureStepEventHandler(step, playerTemp, team);

        for (const controlStructureStep of controlStructureResponse ?? []) {
          playerTemp = await executeStep(playerTemp, controlStructureStep);
        }
      } else {
        // execute regular step
        playerTemp = await executeStep(playerTemp, step);
      }
    }

    // run all steps first and save updated player state in the end to avoid race condition
    // in event
    return playerTemp;
  };

  const executeStep = async (
    playerTemp: PlayerContextState,
    step: AlgorithmStep | AlgorithmControlStructure
  ) => {
    if (step.operation === 'openTask' || step.operation === 'openRandomTask') {
      const taskId = (step.argumentAssetId ?? '') as string;
      const selectedTask = GetTaskById(gameDocument.gameDocument, taskId);

      //prevent execute openTask or openRandomTask if the task is QR Task
      if (selectedTask?.enableQRCode) return playerTemp;
    }

    let awardedScore = 0;
    let scoreToAward = 0;
    if (step.operation === 'giveScore(score)') {
      const playerTask = GetPlayerTaskById(
        playerTemp,
        playerTemp.playerState?.latestCompletedTaskId ?? ''
      );
      awardedScore =
        playerTask?.taskContentFormAnswers?.reduce(
          (total, item) => total + (item.score ?? 0),
          0
        ) ?? 0;

      scoreToAward =
        (step.useFallbackValue ? awardedScore : step.argumentQuantity) ?? 0;
    }

    if (step.operation === 'resetTask') {
      const taskId = (step.argumentAssetId ?? '') as string;
      const selectedTask = GetTaskById(gameDocument.gameDocument, taskId);

      if (selectedTask) {
        const executedStep: string[] = [];
        selectedTask.events?.onOpenTask?.steps?.forEach((e) =>
          executedStep.push(e.id)
        );
        selectedTask.events?.onCloseTask?.steps?.forEach((e) =>
          executedStep.push(e.id)
        );
        selectedTask.events?.onCompleteTask?.steps?.forEach((e) =>
          executedStep.push(e.id)
        );
        selectedTask.events?.onProximity?.steps?.forEach((e) =>
          executedStep.push(e.id)
        );

        // remove all event id from executed step in player, so can be executed again
        executedStep.forEach((s) => {
          if (playerTemp.playerState?.executedStepIds) {
            playerTemp.playerState.executedStepIds =
              playerTemp.playerState.executedStepIds.filter((e) => e !== s);
          }
        });
      }
    }

    if (step.operation === 'distributeTask') {
      const result = await distributeTaskAsync(
        gameDocument?.gameCode!,
        player?.playerState?.code!,
        {
          stepId: step?.id,
          taskIds: step?.argumentAssetId as string[],
          quantity: step?.argumentQuantity
        }
      );

      for (const taskId of result) {
        const distributedTask = await ShowTaskAsync(playerTemp, taskId);
        playerTemp = UpdatePlayerStateAsync(playerTemp, distributedTask);
      }

      if (step.identifier === 'team' && teamCode) {
        await TeamAlgorithmHandler(
          gameDocument,
          team,
          playerTemp,
          step,
          gameConnection?.connectionId ?? ''
        );
        postTeamStateAsync(gameDocument?.gameCode!, teamCode);
      }
    } else if (step.operation === 'assignTitle') {
      let result = '';

      if (step.identifier === 'player') {
        result = await distributeTitleAsync(
          gameDocument.gameCode!,
          player.playerState?.code!,
          {
            stepId: step.id,
            titleIds: step.argumentAssetId as string[]
          }
        );
      }

      if (step.identifier === 'team' && teamCode) {
        result = await teamDistributeTitleAsync(
          team?.gameCode!,
          teamCode,
          {
            stepId: step.id,
            titleIds: step.argumentAssetId as string[]
          },
          gameConnection?.connectionId ?? ''
        );
      }

      if (result !== '') {
        const response = AddTitle(playerTemp, result);
        playerTemp = UpdatePlayerStateAsync(playerTemp, response);
        addTitleToast(result);
      }
    } else if (step.operation === 'giveScore(score)') {
      if (step.identifier === 'team' && teamCode) {
        teamGiveScore(
          gameDocument?.gameCode!,
          player.playerState?.teamCode!,
          scoreToAward,
          true
        );
      } else {
        const submitScore = GiveScore(playerTemp, awardedScore);
        playerTemp = UpdatePlayerStateAsync(playerTemp, submitScore);

        if (awardedScore !== 0) showScoreNotification(scoreToAward);
      }
    } else {
      const operation = (step.operation ?? '').toLowerCase();

      if (step.identifier === 'team' && teamCode) {
        await TeamAlgorithmHandler(
          gameDocument,
          team,
          playerTemp,
          step,
          gameConnection?.connectionId ?? ''
        );
        postTeamStateAsync(gameDocument?.gameCode!, teamCode);
      }

      // team score, team title, team item
      if (
        step.identifier === 'team' &&
        teamCode &&
        (operation.includes('score') ||
          operation.includes('title') ||
          operation.includes('item'))
      ) {
        const teamOperation = step.operation ?? '';
        if (teamOperation.toLowerCase().includes('score')) {
          const score = step.argumentQuantity ?? 0;
          setTeam((prev) => {
            const response =
              teamOperation === 'giveScore'
                ? TeamGiveScore(prev.teamState!, score)
                : TeamRemoveScore(prev.teamState!, score);
            return UpdateTeamStateAsync(prev, response);
          });

          if (teamOperation === 'giveScore') addScoreToast(score, true);
          if (teamOperation === 'removeScore') removeScoreToast(score, true);
        } else if (teamOperation.toLowerCase().includes('title')) {
          const title = step.argumentAssetId as string;
          setTeam((prev) => {
            const response =
              teamOperation === 'giveTitle'
                ? TeamGiveTitleAsync(prev.teamState!, title)
                : TeamRemoveTitleAsync(prev.teamState!, title);
            return UpdateTeamStateAsync(prev, response);
          });

          if (teamOperation === 'giveTitle') addTitleToast(title, true);
          if (teamOperation === 'removeTitle') removeTitleToast(title, true);
        } else if (teamOperation.toLowerCase().includes('item')) {
          const item = step.argumentAssetId as string;
          const quantity = step.argumentQuantity ?? 0;
          setTeam((prev) => {
            const response =
              teamOperation === 'giveItem'
                ? TeamGiveItemAsync(prev, item, quantity)
                : TeamRemoveItemAsync(prev, item, quantity);
            return UpdateTeamStateAsync(prev, response);
          });

          if (teamOperation === 'giveItem') addItemToast(item, quantity, true);
          if (teamOperation === 'removeItem')
            removeItemToast(item, quantity, true);
        }
      } else {
        let bankResult: BankActionResponse | null = null;
        if (operation.includes('item') || operation.includes('stock')) {
          let quantity = step.argumentQuantity ?? 0;

          if (operation === 'removebankstock') {
            const bankItem = (await apiFetcher(
              `games/${gameCode}/bank/${step.argumentAssetId}`
            )) as BankItem;

            // as per AC 8835, when removing an item via event action:
            // if the requested quantity is greater than the current item stock, set the stock to 0.
            // replace the requested quantity with the current item stock
            // so the API wont return an "Insufficient stock" error.
            if (bankItem.currentStock < quantity)
              quantity = bankItem.currentStock;
          }

          bankResult = await trigger({
            action: ['giveitem', 'removebankstock'].includes(operation)
              ? 'acquire'
              : 'return',
            actor: operation.includes('stock')
              ? `Event&Action-${playerTemp.playerState?.code}`
              : (playerTemp.playerState?.name ?? ''),
            actorId: operation.includes('stock')
              ? `${step.id}`
              : (playerTemp.playerState?.code ?? ''),
            itemId: (step.argumentAssetId as string) ?? '',
            quantity
          });

          if (!bankResult.success) return playerTemp;
        }

        const executedStep = PlayerAlgorithmHandler(
          gameDocument,
          playerTemp,
          step
        );

        playerTemp = UpdatePlayerStateAsync(playerTemp, executedStep);
        await toastHandler(playerTemp, step);
      }
    }

    return playerTemp;
  };

  return {
    executeTaskEvent,
    executeAreaEvent,
    executeTimerEvent,
    executeMapOrZoneEvent
  };
};
